import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import {
  supportsFileSystemAccess,
  getParentFolder,
  getAssetsFolder,
  writeBlobToFolder,
  getStoredFolderName,
} from '../utils/pdfSave';

// ── Design tokens (mirrors ExpensesPanel) ─────────────────────────────────
const NAVY    = '#1E3A4A';
const BORDER  = '#E5E7EB';
const MUTED   = '#64748B';
const TEXT    = '#0F172A';

const METHOD_LABELS = {
  instant_writeoff:   'Instant Write-off',
  prime_cost:         'Prime Cost',
  diminishing_value:  'Diminishing Value',
};
const STATUS_LABELS = {
  active:   'Active',
  sold:     'Sold',
  disposed: 'Disposed',
  lost:     'Lost',
};
const STATUS_COLORS = {
  active:   { bg: '#D1FAE5', fg: '#065F46' },
  sold:     { bg: '#DBEAFE', fg: '#1E40AF' },
  disposed: { bg: '#FEE2E2', fg: '#991B1B' },
  lost:     { bg: '#FEF3C7', fg: '#92400E' },
};

function todayIso() { return new Date().toISOString().slice(0, 10); }
function fmtMoney(n)  { return '$' + (Number(n) || 0).toFixed(2); }
function fmtMoney0(n) { return '$' + Math.round(Number(n) || 0).toLocaleString('en-AU'); }
function fmtDateDMY(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
function fyEndOf(iso) {
  if (!iso) return null;
  const [y, m] = iso.split('-').map(Number);
  return m >= 7 ? y + 1 : y;
}
function currentFyEnd() { return fyEndOf(todayIso()); }
function slugify(s) {
  return String(s || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}
function buildAssetFilename({ purchase_date, name, total }, ext) {
  const d   = fmtDateDMY(purchase_date).replace(/\//g, '-');
  const n   = slugify(name) || 'ASSET';
  const tot = '$' + Math.round(Number(total) || 0);
  const e   = (ext || 'pdf').replace(/^\./, '').toLowerCase();
  return `${d}_${n}_${tot}.${e}`;
}

// ── Main component ─────────────────────────────────────────────────────────
export default function AssetsPanel() {
  const [meta, setMeta] = useState({ categories: [], statuses: ['active','sold','disposed','lost'], methods: ['instant_writeoff','prime_cost','diminishing_value'], instant_writeoff_threshold: 20000 });
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);

  const [filterStatus,   setFilterStatus]   = useState('active');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFy,       setFilterFy]       = useState(currentFyEnd());

  const [editing, setEditing] = useState(null);
  const [folderName, setFolderName] = useState(null);

  useEffect(() => {
    api.getAssetMeta().then(setMeta).catch(console.error);
    getStoredFolderName().then(setFolderName).catch(() => setFolderName(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { fy: filterFy };
      if (filterStatus)   params.status   = filterStatus;
      if (filterCategory) params.category = filterCategory;
      const [rowsRes, totalsRes] = await Promise.all([
        api.getAssets(params),
        api.getAssetTotals({ fy: filterFy }),
      ]);
      setRows(rowsRes);
      setTotals(totalsRes);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filterFy, filterStatus, filterCategory]);

  useEffect(() => { load(); }, [load]);

  function handleNew() {
    setEditing({
      _new: true,
      name: '', category: meta.categories[0] || 'Equipment',
      purchase_date: todayIso(),
      cost_ex_gst: '', has_gst: true,
      vendor: '', invoice_number: '',
      status: 'active',
      depreciation_method: 'instant_writeoff',
      useful_life_years: '',
      location: '', serial_number: '',
      receipt_filename: '', receipt_folder: '',
      photo_filename: '',   photo_folder: '',
      notes: '',
    });
  }

  return (
    <div>
      {/* ── Quick stats ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12, marginBottom: 14,
      }}>
        <StatCard label="Total assets" value={String(totals?.total_assets ?? 0)}
          sub={`${totals?.active_count ?? 0} active · ${totals?.disposed_count ?? 0} disposed`} />
        <StatCard label="Active value (ex GST)"  value={fmtMoney0(totals?.total_active_value ?? 0)} />
        <StatCard
          label={`Deduction · FY ${filterFy - 1}–${String(filterFy).slice(2)}`}
          value={fmtMoney0(totals?.total_deduction_fy ?? 0)}
          sub="across all assets"
        />
        <StatCard label="Instant write-off cap" value={fmtMoney0(meta.instant_writeoff_threshold)}
          sub="per asset (small business)" />
      </div>

      {/* ── Filter bar ───────────────────────────────────────────────────── */}
      <div style={{
        background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12,
        padding: 12, marginBottom: 14, display: 'flex', flexWrap: 'wrap',
        gap: 10, alignItems: 'center',
      }}>
        <select value={filterFy} onChange={e => setFilterFy(parseInt(e.target.value))} style={selectStyle}>
          {[currentFyEnd(), currentFyEnd() - 1, currentFyEnd() - 2].map(y =>
            <option key={y} value={y}>FY {y - 1}–{String(y).slice(2)}</option>
          )}
        </select>

        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
          <option value="">All statuses</option>
          {meta.statuses.map(s => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
        </select>

        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={selectStyle}>
          <option value="">All categories</option>
          {meta.categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {(filterStatus || filterCategory) && filterStatus !== 'active' && (
          <button onClick={() => { setFilterStatus('active'); setFilterCategory(''); }} style={{ ...btnGhost, color: '#dc2626' }}>
            ✕ Reset
          </button>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {supportsFileSystemAccess() && (
            <span style={{ fontSize: 11, color: MUTED }}>
              📁 Files → {folderName || '(pick on first save)'} / ASSETS /
            </span>
          )}
          <button onClick={handleNew} style={btnPrimary}>+ Add Asset</button>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: MUTED, fontSize: 13 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 }}>
            No assets for these filters.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: `1px solid ${BORDER}` }}>
                <Th>Asset</Th>
                <Th>Category</Th>
                <Th>Purchase</Th>
                <Th align="right">Cost ex GST</Th>
                <Th>Method</Th>
                <Th align="right">FY deduction</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <Td>
                    <div style={{ fontWeight: 700, color: TEXT }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>
                      {r.vendor || '—'}
                      {r.serial_number ? ` · S/N ${r.serial_number}` : ''}
                      {r.location ? ` · ${r.location}` : ''}
                    </div>
                  </Td>
                  <Td><CategoryPill name={r.category} /></Td>
                  <Td>{fmtDateDMY(r.purchase_date)}</Td>
                  <Td align="right" style={{ fontWeight: 700 }}>{fmtMoney(r.cost_ex_gst)}</Td>
                  <Td style={{ fontSize: 11, color: MUTED }}>
                    {METHOD_LABELS[r.depreciation_method] || r.depreciation_method}
                    {r.useful_life_years ? ` · ${r.useful_life_years}y` : ''}
                  </Td>
                  <Td align="right" style={{ fontWeight: 700, color: r.fy_deduction > 0 ? '#16a34a' : MUTED }}>
                    {fmtMoney(r.fy_deduction || 0)}
                  </Td>
                  <Td><StatusPill status={r.status} /></Td>
                  <Td align="right" style={{ whiteSpace: 'nowrap' }}>
                    <button onClick={() => setEditing({ ...r, has_gst: Number(r.gst) > 0 })} style={btnGhost}>Edit</button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Delete this asset? The receipt file (if any) on OneDrive will not be deleted.')) return;
                        try { await api.deleteAsset(r.id); load(); }
                        catch (e) { alert(e.message); }
                      }}
                      style={{ ...btnGhost, color: '#dc2626', marginLeft: 4 }}
                    >Delete</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal ───────────────────────────────────────────────────────── */}
      {editing && (
        <AssetModal
          row={editing}
          meta={meta}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: '#FAFAF7', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px 18px' }}>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: TEXT, letterSpacing: -0.3 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function Th({ children, align = 'left' }) {
  return (
    <th style={{
      padding: '10px 12px', fontSize: 11, fontWeight: 700, color: MUTED,
      textAlign: align, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
    }}>{children}</th>
  );
}
function Td({ children, align = 'left', style }) {
  return <td style={{ padding: '10px 12px', textAlign: align, color: TEXT, ...style }}>{children}</td>;
}

const CATEGORY_COLORS = {
  Equipment: { bg: '#DBEAFE', fg: '#1E40AF' },
  Tools:     { bg: '#FEF3C7', fg: '#92400E' },
  Vehicle:   { bg: '#FEE2E2', fg: '#991B1B' },
  IT:        { bg: '#F3E8FF', fg: '#6B21A8' },
  Office:    { bg: '#D1FAE5', fg: '#065F46' },
  Other:     { bg: '#F3F4F6', fg: '#374151' },
};
function CategoryPill({ name }) {
  const c = CATEGORY_COLORS[name] || CATEGORY_COLORS.Other;
  return (
    <span style={{ background: c.bg, color: c.fg, padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {name}
    </span>
  );
}
function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.active;
  return (
    <span style={{ background: c.bg, color: c.fg, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────
function AssetModal({ row, meta, onCancel, onSaved }) {
  const isNew = !!row._new;
  const [name,            setName]            = useState(row.name || '');
  const [category,        setCategory]        = useState(row.category || meta.categories[0] || 'Equipment');
  const [purchaseDate,    setPurchaseDate]    = useState(row.purchase_date || todayIso());
  const [costEx,          setCostEx]          = useState(String(row.cost_ex_gst ?? ''));
  const [hasGst,          setHasGst]          = useState(row.has_gst ?? Number(row.gst) > 0 ?? true);
  const [vendor,          setVendor]          = useState(row.vendor || '');
  const [invoiceNumber,   setInvoiceNumber]   = useState(row.invoice_number || '');
  const [status,          setStatus]          = useState(row.status || 'active');
  const [method,          setMethod]          = useState(row.depreciation_method || 'instant_writeoff');
  const [usefulLife,      setUsefulLife]      = useState(String(row.useful_life_years ?? ''));
  const [location,        setLocation]        = useState(row.location || '');
  const [serialNumber,    setSerialNumber]    = useState(row.serial_number || '');
  const [disposalDate,    setDisposalDate]    = useState(row.disposal_date || '');
  const [disposalValue,   setDisposalValue]   = useState(String(row.disposal_value ?? ''));
  const [notes,           setNotes]           = useState(row.notes || '');

  const [receiptFile,     setReceiptFile]     = useState(null);
  const [receiptFilename, setReceiptFilename] = useState(row.receipt_filename || '');
  const [receiptFolder,   setReceiptFolder]   = useState(row.receipt_folder || '');
  const [photoFile,       setPhotoFile]       = useState(null);
  const [photoFilename,   setPhotoFilename]   = useState(row.photo_filename || '');
  const [photoFolder,     setPhotoFolder]     = useState(row.photo_folder || '');

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const exNum    = parseFloat(costEx) || 0;
  const gstNum   = hasGst ? +(exNum * 0.1).toFixed(2) : 0;
  const totalNum = +(exNum + gstNum).toFixed(2);

  const overThreshold = exNum > meta.instant_writeoff_threshold;
  const lifeRequired  = method !== 'instant_writeoff';
  const lifeNum       = parseInt(usefulLife) || 0;
  const isDisposed    = status !== 'active';

  // Live depreciation preview (current FY only)
  const depPreview = useMemo(() => {
    if (!exNum) return null;
    const purchaseFy = fyEndOf(purchaseDate);
    if (!purchaseFy) return null;
    const currentFy = currentFyEnd();
    if (method === 'instant_writeoff') {
      return purchaseFy === currentFy ? exNum : 0;
    }
    if (!lifeNum) return null;
    if (method === 'prime_cost') {
      if (currentFy >= purchaseFy && currentFy < purchaseFy + lifeNum) return +(exNum / lifeNum).toFixed(2);
      return 0;
    }
    if (method === 'diminishing_value') {
      const rate = 2 / lifeNum;
      let book = exNum;
      for (let y = purchaseFy; y <= currentFy; y++) {
        const dep = +(book * rate).toFixed(2);
        if (y === currentFy) return Math.min(dep, book);
        book = Math.max(0, book - dep);
      }
    }
    return null;
  }, [exNum, purchaseDate, method, lifeNum]);

  function handlePickFile(setter) {
    return (e) => {
      const f = e.target.files?.[0];
      if (f) setter(f);
      e.target.value = '';
    };
  }

  async function handleSave() {
    setError('');
    if (!name.trim())        { setError('Asset name is required.'); return; }
    if (!exNum || exNum <= 0){ setError('Cost must be greater than zero.'); return; }
    if (lifeRequired && (!lifeNum || lifeNum <= 0)) {
      setError('Useful life (years) is required for Prime Cost / Diminishing Value.');
      return;
    }
    if (isDisposed && !disposalDate) {
      setError('Disposal date is required when status is sold / disposed / lost.');
      return;
    }

    setSaving(true);
    try {
      // Step 1: write receipt + photo to OneDrive while user gesture is live.
      let savedReceipt = receiptFilename, savedReceiptFolder = receiptFolder;
      let savedPhoto   = photoFilename,   savedPhotoFolder   = photoFolder;

      if ((receiptFile || photoFile) && supportsFileSystemAccess()) {
        const parent = await getParentFolder();
        if (!parent) throw new Error('No OneDrive parent folder selected.');

        if (receiptFile) {
          const folder = await getAssetsFolder(parent, 'Receipts');
          const ext = (receiptFile.name.match(/\.([^.]+)$/) || [, 'pdf'])[1];
          const fname = buildAssetFilename({ purchase_date: purchaseDate, name, total: totalNum }, ext);
          await writeBlobToFolder(folder, fname, receiptFile);
          savedReceipt = fname;
          savedReceiptFolder = 'ASSETS / Receipts';
        }
        if (photoFile) {
          const folder = await getAssetsFolder(parent, 'Photos');
          const ext = (photoFile.name.match(/\.([^.]+)$/) || [, 'jpg'])[1];
          const fname = buildAssetFilename({ purchase_date: purchaseDate, name, total: totalNum }, ext);
          await writeBlobToFolder(folder, fname, photoFile);
          savedPhoto = fname;
          savedPhotoFolder = 'ASSETS / Photos';
        }
      }

      const body = {
        name: name.trim(),
        category, purchase_date: purchaseDate,
        cost_ex_gst: exNum, gst: gstNum, total_inc_gst: totalNum,
        vendor: vendor.trim() || null,
        invoice_number: invoiceNumber.trim() || null,
        status,
        depreciation_method: method,
        useful_life_years: lifeRequired ? lifeNum : null,
        location: location.trim() || null,
        serial_number: serialNumber.trim() || null,
        disposal_date: isDisposed ? disposalDate : null,
        disposal_value: isDisposed && disposalValue !== '' ? parseFloat(disposalValue) : null,
        receipt_filename: savedReceipt || null,
        receipt_folder:   savedReceiptFolder || null,
        photo_filename:   savedPhoto || null,
        photo_folder:     savedPhotoFolder || null,
        notes: notes.trim() || null,
      };

      if (isNew) await api.createAsset(body);
      else        await api.updateAsset(row.id, body);
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={modalBackdrop}>
      <div style={modalShell}>
        <div style={modalHeader}>
          <div style={{ fontSize: 16, fontWeight: 800, color: TEXT }}>
            {isNew ? 'Add Asset' : 'Edit Asset'}
          </div>
          <button onClick={onCancel} disabled={saving} style={iconClose}>✕</button>
        </div>

        <div style={modalBody}>
          {error && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <Field label="Asset name *">
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Polisher Rupes BigFoot LHR15 Mark III" style={inputStyle} />
          </Field>

          <Row2>
            <Field label="Category">
              <select value={category} onChange={e => setCategory(e.target.value)} style={selectStyle}>
                {meta.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Purchase date *">
              <input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} style={inputStyle} />
            </Field>
          </Row2>

          <Row2>
            <Field label="Cost (ex GST) *">
              <input type="number" step="0.01" min="0" value={costEx}
                onChange={e => setCostEx(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="GST">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: TEXT, padding: '8px 0' }}>
                <input type="checkbox" checked={hasGst} onChange={e => setHasGst(e.target.checked)} />
                GST applicable (auto 10%)
              </label>
            </Field>
          </Row2>

          <div style={{ background: '#F8FAFC', border: `1px solid ${BORDER}`, borderRadius: 8, padding: 10,
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
            <Calc label="Ex GST" value={fmtMoney(exNum)} />
            <Calc label="+ GST"  value={fmtMoney(gstNum)} muted />
            <Calc label="Total inc GST" value={fmtMoney(totalNum)} bold />
          </div>

          <Row2>
            <Field label="Vendor">
              <input type="text" value={vendor} onChange={e => setVendor(e.target.value)}
                placeholder="e.g. Repco, Bunnings" style={inputStyle} />
            </Field>
            <Field label="Invoice number">
              <input type="text" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)}
                style={inputStyle} />
            </Field>
          </Row2>

          {/* Depreciation block */}
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: 12, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#92400E', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
              Depreciation
            </div>
            <Row2>
              <Field label="Method">
                <select value={method} onChange={e => setMethod(e.target.value)} style={selectStyle}>
                  {meta.methods.map(m => <option key={m} value={m}>{METHOD_LABELS[m] || m}</option>)}
                </select>
                {overThreshold && method === 'instant_writeoff' && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                    ⚠️ This asset is over the {fmtMoney0(meta.instant_writeoff_threshold)} threshold — instant write-off may not apply. Confirm with your accountant.
                  </div>
                )}
              </Field>
              <Field label={`Useful life (years)${lifeRequired ? ' *' : ''}`}>
                <input type="number" min="1" max="40" value={usefulLife}
                  disabled={!lifeRequired}
                  onChange={e => setUsefulLife(e.target.value)}
                  placeholder={lifeRequired ? 'e.g. 5' : 'N/A'}
                  style={{ ...inputStyle, opacity: lifeRequired ? 1 : 0.5 }} />
              </Field>
            </Row2>
            {depPreview != null && (
              <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
                Estimated deduction this FY:&nbsp;
                <strong style={{ color: '#16a34a' }}>{fmtMoney(depPreview)}</strong>
              </div>
            )}
          </div>

          <Row2>
            <Field label="Location">
              <input type="text" value={location} onChange={e => setLocation(e.target.value)}
                placeholder="e.g. Workshop, Truck 1" style={inputStyle} />
            </Field>
            <Field label="Serial number">
              <input type="text" value={serialNumber} onChange={e => setSerialNumber(e.target.value)}
                style={inputStyle} />
            </Field>
          </Row2>

          {/* Status / disposal */}
          <Row2>
            <Field label="Status">
              <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
                {meta.statuses.map(s => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
              </select>
            </Field>
            {isDisposed ? (
              <Field label={`${STATUS_LABELS[status]} date *`}>
                <input type="date" value={disposalDate} onChange={e => setDisposalDate(e.target.value)} style={inputStyle} />
              </Field>
            ) : <div />}
          </Row2>

          {isDisposed && status === 'sold' && (
            <Field label="Sale value">
              <input type="number" step="0.01" min="0" value={disposalValue}
                onChange={e => setDisposalValue(e.target.value)}
                placeholder="e.g. 350.00" style={inputStyle} />
              {disposalValue && exNum > 0 && (
                <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                  Capital {parseFloat(disposalValue) >= exNum ? 'gain' : 'loss'}:&nbsp;
                  <strong style={{ color: parseFloat(disposalValue) >= exNum ? '#16a34a' : '#dc2626' }}>
                    {fmtMoney(parseFloat(disposalValue) - exNum)}
                  </strong>
                </div>
              )}
            </Field>
          )}

          <Row2>
            <Field label="Receipt (PDF/JPG/PNG)">
              <input type="file" accept="application/pdf,image/png,image/jpeg" onChange={handlePickFile(setReceiptFile)}
                style={{ ...inputStyle, padding: '6px 8px' }} />
              {receiptFile && (
                <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>
                  ✓ {receiptFile.name} — saves to ASSETS / Receipts
                </div>
              )}
              {!receiptFile && receiptFilename && (
                <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                  📎 {receiptFilename} ({receiptFolder})
                </div>
              )}
            </Field>
            <Field label="Photo (JPG/PNG)">
              <input type="file" accept="image/png,image/jpeg" onChange={handlePickFile(setPhotoFile)}
                style={{ ...inputStyle, padding: '6px 8px' }} />
              {photoFile && (
                <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>
                  ✓ {photoFile.name} — saves to ASSETS / Photos
                </div>
              )}
              {!photoFile && photoFilename && (
                <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                  🖼 {photoFilename} ({photoFolder})
                </div>
              )}
            </Field>
          </Row2>

          <Field label="Notes">
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Warranty, accessories, condition, etc."
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </Field>
        </div>

        <div style={modalFooter}>
          <button onClick={onCancel} disabled={saving} style={btnGhost}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>
            {saving ? 'Saving…' : isNew ? 'Save asset' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reused field / row components (mirror ExpensesPanel for consistency)
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
function Row2({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}
function Calc({ label, value, muted, bold }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: bold ? 16 : 14, fontWeight: bold ? 800 : 600, color: muted ? MUTED : TEXT }}>{value}</div>
    </div>
  );
}

// Style tokens
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: `1px solid ${BORDER}`, borderRadius: 8, background: '#fff',
  color: TEXT, boxSizing: 'border-box', outline: 'none',
};
const selectStyle = { ...inputStyle, cursor: 'pointer' };
const btnPrimary = {
  padding: '8px 18px', borderRadius: 8, border: 'none',
  background: NAVY, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
};
const btnGhost = {
  padding: '6px 12px', borderRadius: 6, border: `1px solid ${BORDER}`,
  background: '#fff', color: TEXT, fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 600,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const modalShell = {
  background: '#fff', borderRadius: 14, width: '100%', maxWidth: 680,
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const modalHeader = {
  padding: '16px 20px', borderBottom: `1px solid ${BORDER}`,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
};
const modalBody = { padding: '16px 20px', overflowY: 'auto', flex: 1 };
const modalFooter = {
  padding: '14px 20px', borderTop: `1px solid ${BORDER}`,
  display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0,
};
const iconClose = {
  background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: 18, padding: 4,
};
